
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ErrorCode } from '@/resources/error/ErrorCode';
import CameraPreview, { getCurrentCameraPreview } from '@/components/CameraPreview.vue';
import CameraPageSensorMotion from '@/components/CameraPageSensorMotion.vue';
import ModalDefault from '@/components/ModalDefault.vue';
import ModalDefaultPopupBodyPermission from '@/components/ModalDefaultPopupBodyPermission.vue';
import { IdealSize } from '@/store/modules/camera';
import { getOSType, osMajorVersion, osMinorVersion, OsType } from '@/util/device';

@Component({
  components: {
    CameraPreview,
    CameraPageSensorMotion,
    ModalDefault,
    ModalDefaultPopupBodyPermission
  }
})
export default class Page extends Vue {
  private isInitialized: boolean = false;
  private isCameraUnmount: boolean = false;
  private cameraMountTimerId: number = -1;
  private handleResizeTimerId: number = -1;
  private isAlreadyPermitted: boolean = false;
  private isModalTryToCameraAccessOpen: boolean = false;
  private isModalErrorPermissionDeniedOpen: boolean = false;
  private isModalErrorUnsupportDeviceOpen: boolean = false;
  private resizeObserver: ResizeObserver | null = null;

  @Watch('facingMode')
  handleFacingMode(): void {
    this.isCameraUnmount = true;
  }

  @Watch('idealSize')
  handleIdealSize(): void {
    this.isCameraUnmount = true;
  }

  @Watch('$route')
  handleRouteChange(): void {
    if (this.isCameraStopByPageTransition) this.isCameraUnmount = true;
  }

  // カメラ切り替え時にCameraPreviewを一度アンマウントすることにより初期化する
  @Watch('isCameraUnmount')
  handleCameraMount(value: boolean): void {
    if (!value) return;
    this.isInitialized = false;
    window.clearTimeout(this.cameraMountTimerId);
    this.cameraMountTimerId = window.setTimeout((): void => {
      this.isCameraUnmount = false;
    }, 100);
  }

  @Watch('isNotFound')
  handleNotFound(value: boolean) {
    if (value) {
      const code: ErrorCode = ErrorCode.Unknown;
      this.$router.replace({ name: 'application-error', params: { code } });
    }
  }

  // 撮影トリガー
  @Watch('isShooted')
  private handleShooted(value: boolean): void {
    if (value) {
      getCurrentCameraPreview()?.pauseVideo();
    } else {
      getCurrentCameraPreview()?.replayVideo();
    }
  }

  @Watch('pictureHeight')
  private handlePictureHeight(height: number): void {
    this.handleResize();
  }

  private get isCameraMount(): boolean {
    if (this.facingMode === 'none') return false;
    // カメラの状態が変わる時は一旦アンマウントする
    if (this.isCameraUnmount) return false;
    // カメラを使わないページでリロードしたらとりあえずマウントしない
    if (!this.isCameraUse && !this.isInitialized) return false;
    // こちらで用意したパーミッションダイアログの表示中はマウントしない
    if (!this.isPermittedCameraAccess) return false;
    return true;
  }

  private get isNotFound(): boolean {
    return this.$store.state.camera.isNotFound;
  }

  private get isShooted(): boolean {
    return this.$store.state.camera.isShooted;
  }

  private get isPC(): boolean {
    return this.$store.getters['camera/isPC'];
  }

  private get isPermittedCameraAccess() {
    return this.$store.getters['camera/isPermittedCameraAccess'];
  }

  private get isGpDocPage(): boolean {
    return this.$store.getters['camera/isGpdoc'];
  }

  private get isOpDocPage(): boolean {
    return this.$store.getters['camera/isOpdoc'];
  }

  private get facingMode(): VideoFacingModeEnum | 'none' {
    return this.$store.getters['camera/facingMode'];
  }

  private get idealSize(): IdealSize {
    return this.$store.getters['camera/idealSize'];
  }

  private get isCameraUse(): boolean {
    const isFaceAction: boolean = this.$store.state.camera.shootType === 'face-action';
    if (this.facingMode === 'none') return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isGpDocPage) return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isOpDocPage) return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && isFaceAction) return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name) return true;
    if (this.$route.name === this.$store.getters['camera/locationShootTypeConfirmation'].name) return true;
    if (this.$route.name === this.$store.getters['camera/locationShoot'].name) return true;
    if (this.$route.name === this.$store.getters['camera/locationConfirmation'].name && this.isCameraStopByPageTransition) return true; // eslint-disable-line prettier/prettier
    if (this.$route.name === this.$store.getters['camera/locationError'].name) return true;
    return false;
  }

  private get backgroundStyle(): string {
    const isFaceAction: boolean = this.$store.state.camera.shootType === 'face-action';
    if (this.$route.name === this.$store.getters['camera/locationShoot'].name) return 'background-color: transparent;';
    if (this.$route.name === this.$store.getters['camera/locationConfirmation'].name) return 'background-color: white;';
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isGpDocPage) return 'background-color: white;'; // eslint-disable-line prettier/prettier
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isOpDocPage) return 'background-color: white;'; // eslint-disable-line prettier/prettier
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && isFaceAction)
      return 'background-color: #4C4C4C;';

    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name)
      return 'background-color: var(--instruction-screen-color); opacity: 0.7;';

    return this.$store.state.camera.isDetectStart
      ? 'background-color: var(--shooting-screen-color); opacity: 0.6;'
      : 'background-color: var(--shooting-auto-screen-color); opacity: 0.7;';
  }

  private get pictureHeight(): number {
    return this.$store.state.camera.pictureHeight;
  }

  /**
   * iOS/iPadOS 17.4, 17.4.1にて、カメラを起動した状態でページ遷移をはさむと、カメラのstreamが失われて黒画面になる。
   * @see https://liquidinc.atlassian.net/browse/EKYCDEV-3827
   */
  private get isCameraStopByPageTransition(): boolean {
    if (getOSType() === OsType.iOS && osMajorVersion === 17 && osMinorVersion === 4) {
      return true;
    }
    if (getOSType() === OsType.iPad && osMajorVersion === 17 && osMinorVersion === 4) {
      return true;
    }
    return false;
  }

  async created(): Promise<void> {
    this.$store.commit('camera/clearSetup');
    window.addEventListener('resize', this.handleResizeScheduler);
    if (window.ResizeObserver) this.resizeObserver = new ResizeObserver(this.handleResizeScheduler);
    this.resizeObserver?.observe(document.body);
    this.handleResize();
    await this.beforeTryToCameraAccess();
  }

  beforeDestroy(): void {
    this.$store.commit('camera/isResize', false);
    window.clearTimeout(this.cameraMountTimerId);
    window.clearTimeout(this.handleResizeTimerId);
    window.removeEventListener('resize', this.handleResizeScheduler);
    this.resizeObserver?.unobserve(document.body);
  }

  private handleResizeScheduler(): void {
    this.$store.commit('camera/isResize', true);
    window.clearTimeout(this.handleResizeTimerId);
    this.handleResizeTimerId = window.setTimeout((): void => {
      this.handleResize();
      this.$store.commit('camera/isResize', false);
    }, 1000);
  }

  private handleResize(): void {
    // 画面サイズ記録
    if (this.isPC) {
      // innerWidthだとスクロールバーの幅も含まれる様なのでclientWidthを使用
      this.$store.commit('camera/displayWidth', document.documentElement.clientWidth);
      this.$store.commit('camera/displayHeight', document.documentElement.clientHeight);
    } else {
      // 手元の端末だとinnerWidthは存在する要素の横幅につられて実際よりも大きな横幅になってしまうことがある
      // 横向きから縦向きに戻した際にinnerWidthだとおかしくなり、その場合はclientWidthを使う方が良さそうである
      // ただしclientWidthはアドレスバーの非表示を検知できず、アドレスバーが隠れると下に隙間ができる
      // そのため、横幅の絶対値はclientWidthが正しく、縦幅の比はinnerHeightが正しいものとして計算する
      const ratio: number = document.documentElement.clientWidth / window.innerWidth;
      this.$store.commit('camera/displayWidth', window.innerWidth * ratio);
      this.$store.commit('camera/displayHeight', window.innerHeight * ratio);
    }

    // 画像サイズ確認
    const videoWidth: number = getCurrentCameraPreview()?.video.videoWidth ?? 0;
    const videoHeight: number = getCurrentCameraPreview()?.video.videoHeight ?? 0;
    if (videoWidth > 0 && videoHeight > 0) {
      // 画像サイズ記録
      this.$store.commit('camera/pictureWidth', videoWidth);
      this.$store.commit('camera/pictureHeight', videoHeight);
    }
  }

  // 初期化完了
  private onInitialize(): void {
    // 画像サイズ確認
    const videoWidth: number = getCurrentCameraPreview()?.video.videoWidth ?? 0;
    const videoHeight: number = getCurrentCameraPreview()?.video.videoHeight ?? 0;
    if (videoWidth > 0 && videoHeight > 0) {
      // 画像サイズ記録
      this.$store.commit('camera/pictureWidth', videoWidth);
      this.$store.commit('camera/pictureHeight', videoHeight);
      // 初期化完了
      this.isInitialized = true;
    }
  }

  private onModalTryToCameraAccessOpen(): void {
    this.isModalTryToCameraAccessOpen = true;
  }

  private onModalTryToCameraAccessClose(): void {
    this.isModalTryToCameraAccessOpen = false;
    this.$store.commit('camera/isPermittedCameraAccess', true);
  }

  private onModalErrorPermissionDeniedOpen(): void {
    this.isModalErrorPermissionDeniedOpen = true;
  }

  private onModalErrorPermissionDeniedClose(): void {
    this.isModalErrorPermissionDeniedOpen = false;
    location.reload();
  }

  private onModalErrorUnsupportDeviceOpen(): void {
    this.isModalErrorUnsupportDeviceOpen = true;
  }

  private onModalErrorUnsupportDeviceClose(): void {
    this.isModalErrorUnsupportDeviceOpen = false;
    location.reload();
  }

  private async checkBrowserCameraPermission(): Promise<boolean> {
    // カメラを許可しているかどうかを確認
    if (navigator.permissions) {
      try {
        const result: PermissionStatus = await navigator.permissions.query({ name: 'camera' } as any); // EKYCDEV-2547 クライアントバージョンアップによりエラーが出るようになったので、動作を変えないようにエラーが出ないようにする。 TODO: チケットを作ってちゃんと修正
        const state: PermissionState = result.state;
        return state === 'granted';
      } catch (error) {}
    }
    return false;
  }

  private async beforeTryToCameraAccess(): Promise<void> {
    // 許可を出している(Vuex)
    if (this.isPermittedCameraAccess) return;

    // 許可を出している(ブラウザ側)
    this.isAlreadyPermitted = await this.checkBrowserCameraPermission();

    if (this.isAlreadyPermitted) {
      this.onModalTryToCameraAccessClose();
    } else {
      this.onModalTryToCameraAccessOpen();
    }
  }
}
